/**
 * Poseidon dashboard — Risk center (§3.2 D).
 *
 * Shows every configured limit alongside its current utilization, the halt
 * controls, the instrument/session allow lists, and the read-only audit log of
 * risk-configuration changes.
 *
 * §3.2 D requires a modification to need a *second* confirmation and to record
 * operator, timestamp, prior value, new value and reason. Both live here: the
 * edit control carries the typed phrase plus a mandatory reason, and the audit
 * table is where the result becomes inspectable.
 */
import { Link } from 'react-router-dom';
import type { AllowListEntryView, RiskConfigAuditRow, RiskLimitRow } from '../api/types';
import { POLL_INTERVALS } from '../config';
import { useApiData } from '../hooks/useApiData';
import { useServices } from '../state/Services';
import { useRibbon } from '../components/AppShell';
import { DataTable } from '../components/DataTable';
import type { TableColumn } from '../components/DataTable';
import { AllowListToggleControl, HaltControls, RiskLimitEditControl } from '../components/Controls';
import { Badge, ErrorState, LoadingState, Panel, ProgressBar, Unavailable } from '../components/primitives';
import { RISK_LIMIT_ORDER, toneForUtilization } from '../lib/labels';
import { formatDateTimeWithZone, formatPercent } from '../lib/format';
import type { Tone } from '../lib/staleness';

export function RiskCenter(): JSX.Element {
  const { api, isMock } = useServices();
  const ribbon = useRibbon();

  const limits = useApiData('risk-limits', () => api.getRiskLimits(), { pollMs: POLL_INTERVALS.riskLimits, mode: api.mode });
  const audit = useApiData('risk-audit', () => api.getRiskAuditLog(), { pollMs: POLL_INTERVALS.riskLimits, mode: api.mode });
  const allowLists = useApiData('risk-allow-lists', () => api.getAllowLists(), { pollMs: POLL_INTERVALS.riskLimits, mode: api.mode });

  const refreshAll = (): void => {
    limits.refresh();
    audit.refresh();
    allowLists.refresh();
  };

  const orderedLimits: RiskLimitRow[] = [...(limits.data ?? [])].sort(
    (left, right) => RISK_LIMIT_ORDER.indexOf(left.limit_id) - RISK_LIMIT_ORDER.indexOf(right.limit_id),
  );

  const limitColumns: readonly TableColumn<RiskLimitRow, 'label' | 'configured' | 'utilization' | 'status' | 'action' | 'version'>[] = [
    {
      key: 'label',
      header: 'Limit',
      sortValue: (row) => row.label,
      render: (row) => (
        <>
          <strong>{row.label}</strong>
          <div className="text-faint" style={{ fontSize: 11 }}>
            {row.category} · unit {row.unit}
          </div>
        </>
      ),
    },
    {
      key: 'configured',
      header: 'Configured value',
      sortValue: (row) => (typeof row.configured_value === 'number' ? row.configured_value : null),
      render: (row) => (
        <>
          <div>{row.configured_display}</div>
          {row.hard_ceiling !== null && <div className="text-faint" style={{ fontSize: 11 }}>hard ceiling {row.hard_ceiling}</div>}
          {row.note !== null && <div className="text-faint" style={{ fontSize: 11 }}>{row.note}</div>}
        </>
      ),
    },
    {
      key: 'utilization',
      header: 'Current utilization',
      sortValue: (row) => row.utilization_pct,
      render: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 }}>
          <span>{row.utilization_display}</span>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <ProgressBar
              fraction={row.utilization_pct}
              tone={row.status === 'breach' ? 'danger' : row.status === 'warning' ? 'warn' : 'ok'}
              title={row.utilization_pct === null ? 'Not measurable yet' : `${formatPercent(row.utilization_pct)} of the configured limit`}
            />
            <span className="text-mono" style={{ fontSize: 11 }}>
              {row.utilization_pct === null ? '—' : formatPercent(row.utilization_pct)}
              {row.utilization_trend !== null && <span className="text-faint"> {row.utilization_trend === 'rising' ? '↑' : row.utilization_trend === 'falling' ? '↓' : '→'}</span>}
            </span>
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status / on breach',
      sortValue: (row) => row.status,
      render: (row) => (
        <>
          <Badge tone={toneForUtilization(row.status)}>{row.status.replace(/_/g, ' ')}</Badge>
          <div className="text-faint" style={{ fontSize: 11 }}>
            action: {row.breach_action.replace(/_/g, ' ')}
          </div>
          {row.requires_second_confirmation && <div className="text-faint" style={{ fontSize: 11 }}>second confirmation required</div>}
        </>
      ),
    },
    {
      key: 'action',
      header: 'Modify',
      render: (row) => <RiskLimitEditControl limit={row} mock={isMock} />,
    },
    {
      key: 'version',
      header: 'Config version / last change',
      sortValue: (row) => row.last_modified_at,
      render: (row) => (
        <>
          <div className="text-mono" style={{ fontSize: 11 }}>{row.config_version}</div>
          <div className="text-faint" style={{ fontSize: 11 }}>
            {row.last_modified_by === null ? 'never changed' : `${row.last_modified_by} · ${formatDateTimeWithZone(row.last_modified_at)}`}
          </div>
        </>
      ),
    },
  ];

  const auditColumns: readonly TableColumn<RiskConfigAuditRow, 'when' | 'operator' | 'target' | 'change' | 'reason' | 'replay'>[] = [
    { key: 'when', header: 'Timestamp', width: '160px', sortValue: (row) => row.occurred_at, render: (row) => <span className="text-mono">{formatDateTimeWithZone(row.occurred_at)}</span> },
    { key: 'operator', header: 'Operator', sortValue: (row) => row.operator, render: (row) => <span className="text-mono">{row.operator}</span> },
    {
      key: 'target',
      header: 'Target',
      sortValue: (row) => row.target,
      render: (row) => (
        <>
          <div>{row.target}</div>
          <div className="text-faint" style={{ fontSize: 11 }}>{row.target_kind.replace(/_/g, ' ')}</div>
        </>
      ),
    },
    {
      key: 'change',
      header: 'Prior value → new value',
      render: (row) => (
        <span className="text-mono" style={{ fontSize: 11.5 }}>
          {row.prior_value} → <strong>{row.new_value}</strong>
          <div className="text-faint">
            {row.config_version_before} → {row.config_version_after}
          </div>
        </span>
      ),
    },
    { key: 'reason', header: 'Reason', render: (row) => <span style={{ fontSize: 12 }}>{row.reason}</span> },
    {
      key: 'replay',
      header: 'Replay validation / chain',
      render: (row) => (
        <>
          <Badge tone={row.replay_validation.result === 'pass' ? 'ok' : row.replay_validation.result === 'fail' ? 'danger' : 'warn'}>
            {row.replay_validation.result === null ? 'not applicable' : row.replay_validation.result.replace(/_/g, ' ')}
          </Badge>
          <div className="text-faint" style={{ fontSize: 11 }}>
            {row.replay_validation.performed ? `session: ${row.replay_validation.session_date ?? '—'}` : 'not run — §16.3.3 expects a replay against a historical session'}
          </div>
          <div className="text-mono text-faint" style={{ fontSize: 10.5 }} title="Hash-chained audit record (§11)">
            {row.chain_hash.slice(0, 22)}…
          </div>
        </>
      ),
    },
  ];

  const allowColumns: readonly TableColumn<AllowListEntryView, 'kind' | 'value' | 'enabled' | 'modified' | 'toggle'>[] = [
    { key: 'kind', header: 'List', sortValue: (row) => row.list_kind, render: (row) => row.list_kind.replace(/_/g, ' ') },
    { key: 'value', header: 'Entry', sortValue: (row) => row.value, render: (row) => <span className="text-mono">{row.value}</span> },
    { key: 'enabled', header: 'Enabled', sortValue: (row) => String(row.enabled), render: (row) => <Badge tone={row.enabled ? 'ok' : 'muted'}>{row.enabled ? 'enabled' : 'disabled'}</Badge> },
    {
      key: 'modified',
      header: 'Note / last change',
      render: (row) => (
        <>
          {row.note !== null && <div style={{ fontSize: 12 }}>{row.note}</div>}
          <div className="text-faint" style={{ fontSize: 11 }}>
            {row.last_modified_by === null ? 'never changed' : `${row.last_modified_by} · ${formatDateTimeWithZone(row.last_modified_at)}`}
          </div>
        </>
      ),
    },
    { key: 'toggle', header: 'Modify', render: (row) => <AllowListToggleControl entry={row} /> },
  ];

  const breaches = orderedLimits.filter((limit) => limit.status === 'breach' || limit.status === 'warning');

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-header__title">Risk center</h1>
          <div className="page-header__spec">§3.2 D · configured limits and utilization, halts, allow lists, read-only config audit log</div>
        </div>
        <div className="data-clock">
          {isMock && <span className="mock-note">mock values</span>}
          <button type="button" className="btn btn--sm btn--ghost" onClick={refreshAll}>
            Refresh
          </button>
        </div>
      </header>

      {breaches.length > 0 && (
        <div className="halt-strip halt-strip--soft" style={{ alignItems: 'flex-start' }}>
          <div>
            <strong>
              {breaches.length} limit{breaches.length === 1 ? '' : 's'} at warning or breach
            </strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {breaches.map((limit) => (
                <li key={limit.limit_id}>
                  {limit.label}: {limit.utilization_display} → action{' '}
                  <span className="text-mono">{limit.breach_action.replace(/_/g, ' ')}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <Panel
        title="Halts"
        subtitle="§7.3 · soft halt and hard halt are deliberately distinct"
        mock={isMock}
        footnote="A soft halt blocks new entries but still allows protective changes, exits and cancels. A hard halt blocks everything except the explicit emergency-close workflow."
      >
        {ribbon === null ? (
          <LoadingState label="Waiting for halt state…" />
        ) : (
          <HaltControls halt={ribbon.halt} mock={isMock} />
        )}
      </Panel>

      <Panel
        title="Configured risk limits and utilization"
        subtitle={`${orderedLimits.length} limits from the daemon`}
        mock={isMock}
        flush
        footnote={
          <>
            §7.1: these percentages are conservative <em>starting conditions for a demo test plan</em>, not universal constants. They are
            versioned, and every change is recorded below. Execution mode ({ribbon?.execution_mode ?? 'unknown'}) is governed on the{' '}
            <Link to="/operations">Operations</Link> page.
          </>
        }
      >
        {limits.error !== null && limits.data === null ? (
          <ErrorState error={limits.error} context="Risk limits could not be loaded." onRetry={limits.refresh} />
        ) : limits.data === null ? (
          <LoadingState label="Loading risk limits…" />
        ) : (
          <DataTable
            rows={orderedLimits}
            columns={limitColumns}
            rowKey={(row) => row.limit_id}
            initialSort={{ key: 'label', direction: 'asc' }}
            emptyTitle="No risk limits configured"
            emptyMessage="A risk engine with no configured limits must refuse to trade, not trade freely."
            rowTone={(row): Tone | null => (row.status === 'breach' ? 'danger' : row.status === 'warning' ? 'warn' : null)}
          />
        )}
      </Panel>

      <Panel
        title="Risk configuration audit log"
        subtitle="read-only · operator, timestamp, prior value, new value, reason"
        mock={isMock}
        flush
        footnote="§11: audit records are hash-chained (each row carries the previous record's hash) and are append-only. Corrections are new records, never edits."
      >
        {audit.error !== null && audit.data === null ? (
          <ErrorState error={audit.error} context="The risk audit log could not be loaded." onRetry={audit.refresh} />
        ) : audit.data === null ? (
          <LoadingState label="Loading audit log…" />
        ) : (
          <DataTable
            rows={audit.data}
            columns={auditColumns}
            rowKey={(row) => row.audit_event_id}
            initialSort={{ key: 'when', direction: 'desc' }}
            emptyTitle="No risk configuration changes recorded"
            emptyMessage="An empty log means nothing has been changed since the ledger began, not that changes are untracked."
          />
        )}
      </Panel>

      <Panel title="Instrument and session allow lists" subtitle="§3.1 · modification requires a typed confirmation and a reason" mock={isMock} flush>
        {allowLists.error !== null && allowLists.data === null ? (
          <ErrorState error={allowLists.error} context="Allow lists could not be loaded." onRetry={allowLists.refresh} />
        ) : allowLists.data === null ? (
          <LoadingState label="Loading allow lists…" />
        ) : (
          <DataTable rows={allowLists.data} columns={allowColumns} rowKey={(row) => row.entry_id} initialSort={{ key: 'kind', direction: 'asc' }} emptyTitle="No allow-list entries" />
        )}
      </Panel>

      <Panel title="How a rejection is explained" subtitle="§7.2">
        <p className="text-muted" style={{ margin: 0 }}>
          Rules are evaluated in a fixed order — halts and account mode, MT5/reconciliation/freshness, instrument and session allow-list,
          candidate integrity, spread/volatility/event, concentration/correlation, sizing and daily loss, margin and broker{' '}
          <span className="text-mono">order_check</span>, duplicate/idempotency, then the human-approval gate. Every result is persisted,
          including the rules that passed, so the dashboard can say something specific like{' '}
          <em>“Rejected: EURUSD spread 2.4 pips exceeds configured 1.5-pip maximum.”</em> rather than “risk rejected”.
        </p>
        <p className="text-faint" style={{ marginTop: 10, fontSize: 11.5 }}>
          The example figures in that sentence come from the spec's own wording (§7.2), not from the current account state; current breach
          text is shown per limit above. A limit the daemon cannot measure yet renders as <Unavailable reason="Values the daemon cannot measure yet are never approximated." /> rather
          than as an approximated number.
        </p>
      </Panel>
    </>
  );
}
